import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://evowalkmetrics.com/api';
const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'https://api.evowalk.movilizame.com.ar/auth';
const API_KEY = process.env.REACT_APP_EVOWALK_API_KEY;

// Helper function to create headers with API key
const getHeaders = (additionalHeaders = {}) => {
    return {
        'X-API-Key': API_KEY,
        ...additionalHeaders
    };
};

export const login = async (email, password) => {
    try {
        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);

        const response = await axios.post(`${AUTH_URL}/login`, formData, {
            headers: { 
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('There was an error logging in:', error);
        return { success: false, message: 'Login failed. Please try again later.' };
    }  
};

export const getUserByToken = async (token) => {
    try {
        const response = await axios.get(`${AUTH_URL}/session`, { headers: { Authorization: `Bearer ${token}` } });
        return response.data;                                        
    } catch (error) {
        console.error('There was an error fetching the user data:', error);
        return null;
    }
};

export const getUserByUserId = async (userId) => {
    try {
        const response = await axios.get(`${BASE_URL}/user_info/${userId}`, { 
            headers: getHeaders() 
        });
        return response.data;
    } catch (error) {
        console.error('There was an error fetching the user data:', error);
        return null;
    }
};

export const getPatientsByClinicianId = async (clinicianId) => {
    try {
        const response = await axios.get(`${BASE_URL}/patients/${clinicianId}`, { 
            headers: getHeaders() 
        });
        return response.data;
    } catch (error) {
        console.error('There was an error fetching the patients list:', error);
        return null;
    }
};

export const getPatientByPatientId = async (patientId) => {
    try {
        const response = await axios.get(`${BASE_URL}/patient_info/${patientId}`, { 
            headers: getHeaders() 
        });
        return response.data;
    } catch (error) {
        console.error('There was an error fetching the patient data:', error);
        return null;
    }
};

export const getAllTrialsByPatientId = async (patientId) => {
    try {
        const response = await axios.get(`${BASE_URL}/trials/${patientId}`, { 
            headers: getHeaders() 
        });
        return response.data;
    } catch (error) {
        console.error('There was an error fetching the trials:', error);
        return null;
    }
}

export const getTrialByTrialId = async (trialId) => {
    try {
        const response = await axios.get(`${BASE_URL}/trial/${trialId}`, { 
            headers: getHeaders() 
        });
        return response.data;
    } catch (error) {
        console.error('There was an error fetching trial:', error);
        return null;
    }
}

export const getSummaryDataByTrialId = async (trialId) => {
    try {
        const response = await axios.get(`${BASE_URL}/walk_data_summary/${trialId}`, { 
            headers: getHeaders() 
        });
        return response.data;
    } catch (error) {
        console.error('There was an error fetching summary data:', error);
        return null;
    }
}

export const getRawDataByTrialId = async (trialId) => {
    try {
        const response = await axios.get(`${BASE_URL}/walk_data_raw/${trialId}`, { 
            headers: getHeaders() 
        });
        return response.data;
    } catch (error) {
        console.error('There was an error fetching raw data:', error);
        return null;
    }
}

const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); 
    const day = String(date.getDate()).padStart(2, '0');

    const dateString = `${year}-${month}-${day}`;

    return dateString;
}

export const getDailyDataByWeek = async (patientId, weekStart, weekEnd) => {
    const weekStartStr = getDateString(weekStart);
    const weekEndStr = getDateString(weekEnd);
    try {
        const response = await axios.get(`${BASE_URL}/daily_data_by_week`, {
            headers: getHeaders(),
            params: {
                patient_id: patientId,
                startDate: weekStartStr,
                endDate: weekEndStr
            }
        });
        return response.data;
    } catch(error) {
        console.error('There was an error fetching weekly data:', error);
        return null;
    }
}

export const getDailyDataByMonth = async (patientId, month) => {
    const dateString = getDateString(month);
    try {
        const response = await axios.get(`${BASE_URL}/daily_data_by_month`, {
            headers: getHeaders(),
            params: {
                patient_id: patientId,
                date: dateString
            }
        });
        return response.data;
    } catch(error) {
        console.error('There was an error fetching monthly data:', error);
        return null;
    }
}

export const getDailyDataByYear = async (patientId, year) => {
    const dateString = getDateString(year);
    try {
        const response = await axios.get(`${BASE_URL}/daily_data_by_year`, {
            headers: getHeaders(),
            params: {
                patient_id: patientId,
                date: dateString
            }
        });
        console.log(response.data);
        return response.data;
    } catch(error) {
        console.error('There was an error fetching monthly data:', error);
        return null;
    }
}

export const getDailyDataByRange = async (patientId, startDate, endDate) => {
    const startDateStr = getDateString(startDate);
    const endDateStr = getDateString(endDate);
    try {
        const response = await axios.get(`${BASE_URL}/daily_data_by_range`, {
            headers: getHeaders(),
            params: {
                patient_id: patientId,
                startDate: startDateStr,
                endDate: endDateStr
            }
        });
        return response.data;
    } catch(error) {
        console.error('There was an error fetching range data:', error);
        return null;
    }
}

/**
 * Sends user feedback to the backend API
 * @param {string} feedback - The feedback message from the user
 * @param {string} email - Optional email of the sender (if available)
 * @param {string} name - Optional name of the sender (if available)
 * @returns {Promise<Object>} Response from the server
 */
export const sendFeedback = async (feedback, email = null, name = null) => {
    try {
        const response = await axios.post(`${BASE_URL}/feedback`, {
            feedback: feedback,
            email: email,
            name: name,
            timestamp: new Date().toISOString()
        }, {
            headers: getHeaders()
        });
        return { 
            success: true, 
            message: 'Feedback sent successfully',
            data: response.data 
        };
    } catch(error) {
        console.error('There was an error sending feedback:', error);
        return { 
            success: false, 
            message: 'Failed to send feedback',
            error: error.message 
        };
    }
}

export const getWalkDataHeadersByTrialId = async (trialId) => {
    try {
        const response = await axios.get(`${BASE_URL}/walk_data_headers/${trialId}`, { 
            headers: getHeaders() 
        });
        return response.data;
    } catch (error) {
        console.error('There was an error fetching walk data headers:', error);
        return null;
    }
}

export const analyzeData = async (trialInfo, summaryData, conditionDescription, imageUrls) => {
    try {
        const response = await axios.post(`${BASE_URL}/analyze_data`, {
            trialInfo,
            summaryData,
            conditionDescription,
            imageUrls
        }, {
            headers: getHeaders(),
            timeout: 300000
        });
        return response.data;
    } catch (error) {
        console.error('There was an error analyzing data:', error);
        return null;
    }
}

export const compareTrials = async (trialInfoArray, trialSummaryArray, conditionDescription) => {
    try {
        const response = await axios.post(`${BASE_URL}/compare_trials`, {
            trialInfoArray,
            trialSummaryArray,
            conditionDescription
        }, {
            headers: getHeaders(),
            timeout: 300000
        });
        return response.data;
    } catch (error) {
        console.error('There was an error comparing trials:', error);
        return null;
    }
}
